<template>
  <div
    class="order-item-block"
    :class="{
      'order-item-block--hidden': isHidden,
      'order-item-block--removed': isRemoved,
      'order-item-block--out-of-stock': isOutOfStock,
      'order-item-block--readonly': isReadOnly,
      'order-item-block--transitioning': isTransitioning,
      'order-item-block--merging': isMerging,
    }"
  >
    <a :href="productPath">
      <img
        :src="imageUrl"
        class="order-item-block__image"
        :class="`order-item-block__image--${sizeModifier}`"
        :alt="imageAlt"
        v-data-test-id="'order-item-image'"
      />
    </a>
    <div class="order-item-block__product-info">
      <div class="order-item-block__description">
        <a
          :href="productPath"
          class="order-item-block__title"
          :class="`order-item-block__title--${sizeModifier}`"
          v-data-test-id="'order-item-name'"
          >{{ name }}</a
        >
        <div
          v-for="(line, index) in validDescriptionLines"
          :key="`description-line-${index}`"
          class="order-item-block__description-line"
          v-data-test-id="'order-item-description-line'"
        >
          {{ line }}
        </div>
        <div
          v-if="extraDescriptionLine"
          class="order-item-block__extra-description"
          :class="{
            'order-item-block__extra-description--truncated': isTruncatingExtraDescriptionLine,
          }"
          v-data-test-id="'order-item-extra-description'"
        >
          {{ extraDescriptionLine }}
        </div>
        <div
          v-if="isQuantityVisible"
          class="order-item-block__quantity"
          :class="`order-item-block__quantity--${sizeModifier}`"
        >
          <span class="order-item-block__quantity-text">
            <span
              v-if="!isReadOnly"
              class="order-item-block__quantity-decrease"
              @click="changeQuantity(-1)"
            >
              <span class="order-item-block__minus icon-minus"></span>
            </span>
            <span v-if="isReadOnly" class="order-item-block__quantity-label"
              >{{ $t('quantity') }}:</span
            >
            <span
              class="order-item-block__quantity-number"
              v-data-test-id="'order-item-quantity'"
              >{{ this.quantity }}</span
            >
            <span
              v-if="!isReadOnly"
              class="order-item-block__quantity-increase"
              :class="{ 'order-item-block__quantity-increase--max': hasReachedMaxQuantity }"
              @click="changeQuantity(1)"
            >
              <span class="order-item-block__plus icon-plus"></span>
            </span>
          </span>
        </div>
      </div>
      <div v-if="!isReadOnly" class="order-item-block__remove icon-trash" @click="removeItem"></div>
      <div class="order-item-block__price" v-data-test-id="'order-item-price'">
        {{ itemTotal }}
        <template v-if="recurringPriceText">
          <template v-if="recurringPriceTextSplit.leadingSlashWithSpace"
            >{{ recurringPriceTextSplit.leadingSlashWithSpace }}&nbsp;</template
          >{{ recurringPriceTextSplit.text }}
        </template>
      </div>
      <span class="order-item-block__flex-break-row"></span>
      <div
        v-if="contextMessage"
        class="order-item-block__context-msg"
        :class="{ 'order-item-block__context-msg--readonly': isReadOnly }"
        v-data-test-id="'order-item-context-msg'"
      >
        {{ contextMessage }}
      </div>
      <div
        v-if="isOutOfStock"
        class="order-item-block__out-of-stock"
        :class="`order-item-block__out-of-stock--${sizeModifier}`"
      >
        <p
          class="order-item-block__out-of-stock-msg"
          v-data-test-id="'order-item-out-of-stock-msg'"
        >
          <i class="icon icon--red icon-alert"></i>{{ $t('out_of_stock') }}
        </p>
        <p
          v-if="isBackorderable"
          class="order-item-block__out-of-stock-notification"
          v-data-test-id="'order-item-out-of-stock-cta'"
          @click="emitOutOfStockClick"
        >
          {{ $t('out_of_stock_notification') }}<i class="icon-carousel-arrow"></i>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { formatToCurrency } from '@/helpers/currency-formatter'

export const SIZE_MODIFIERS = {
  default: 'default',
  large: 'large',
}

export default {
  name: 'OrderItemBlock',
  props: {
    imageUrl: { type: String, required: true },
    imageAlt: { type: String },
    name: { type: String, required: true },
    descriptionLines: { type: Array, default: () => [] },
    quantity: { type: Number, default: 1, required: true }, // Adding 1 as default to make sure it doesn't break items where quantity is not defined
    isQuantityVisible: { type: Boolean, default: true },
    hasReachedMaxQuantity: { type: Boolean, default: false },
    price: { type: Number, required: true },
    recurringPriceText: { type: String, default: '' },
    productPath: { type: String, default: '#' },
    isOutOfStock: { type: Boolean, default: false },
    isMerging: { type: Boolean, default: false },
    isTransitioning: { type: Boolean, default: false },
    isReadOnly: { type: Boolean, default: false },
    sizeModifier: {
      type: String,
      default: SIZE_MODIFIERS.default,
      validator(variant) {
        return Object.values(SIZE_MODIFIERS).includes(variant)
      },
    },
    isHidden: { type: Boolean, default: false },
    isRemoved: { type: Boolean, default: false },
    extraDescriptionLine: { type: String, default: '' },
    isTruncatingExtraDescriptionLine: { type: Boolean, default: false },
    contextMessage: { type: String, default: '' },
    isBackorderable: { type: Boolean, default: false },
  },

  computed: {
    ...mapState({
      currency: state => state.store.currency,
      locale: state => state.page.locale,
    }),

    itemTotal() {
      const amount = this.price * this.quantity
      return formatToCurrency(amount, this.locale, this.currency)
    },

    recurringPriceTextSplit() {
      const [, leadingSlashWithSpace = '', text = this.recurringPriceText] =
        this.recurringPriceText.match(/^(\s*\/\s+)?(\S.+)/) || []

      return { leadingSlashWithSpace, text }
    },

    validDescriptionLines() {
      return this.descriptionLines.filter(line => line)
    },
  },

  methods: {
    removeItem() {
      this.$emit('remove-item', this.$el)
    },

    changeQuantity(increase) {
      this.$emit('change-quantity', increase)
    },

    emitOutOfStockClick() {
      this.$emit('out-of-stock-click')
    },
  },
}
</script>

<style lang="scss" scoped>
@import './OrderItemBlock';
</style>

<i18n src="@/locales/order_item.json" />
